<i18n>
{
	"en": {
		"customer": "Customer",
		"cargoCarrier": "Cargo Carrier",
		"fromDespatchParty": "Despatch Party",
		"fromLocation": "From Location",
		"goodsType": "Goods Type",
		"mainDesc": "You can save search settings as a quick search for use in the menu. Select the search criteria below to save the search.",
		"makePublic": "Make this quick search available to other users",
		"orderType": "Order Type",
		"save": "Save",
		"searchCriteria": "Search Criteria",
		"searchCriteriaDesc": "Select the criteria you want the shortcut to use:",
		"searchName": "Name",
		"status": "Status",
		"timeIntervalAfter": "Today | day after present | days after present",
		"timeIntervalBefore": "Today | day before present | days before present",
		"title": "Save Quick Search",
		"toDeliveryParty": "Delivery Party",
		"toLocation": "To Location",
		"tripType": "Trip Type",
		"cancelLabel": "Cancel",
		"failedToSave": "Failed to save search",
		"failedToDelete": "Failed to delete search",
		"savedSearch": "Quicksearch saved",
		"deletedSearch": "Quicksearch deleted",
		"pickupStatus": "Pickup Status",
		"deliveryStatus": "Delivery Status",
		"economicalStatus": "Economical Status",
		"orderStatus": "Operational status",
		"references": "References",
		"searchAreas": "Address / Area",
		"noDaySet": "No day set",
		"before": "Before",
		"after": "After",
		"planned": "Planned",
		"waybillNumber": "Waybill Number",
		"viewMainMenu": "Show this quick search in the main menu",
		"viewMainMenuDesc": "Continually updates the search with a 1 minute interval",
		"orderFilters": "Order filters",
		"tripFilters": "Trip filters",
		"etdFrom": "ETD from",
		"etdTo": "ETD to",
		"noLabel": "Please give this quick search a name",
		"emptyPatch": "Nothing to update",
		"inboundEdi": "Inbound EDI",
		"deviation": "Deviation"
	},
	"no": {
		"customer": "Kunde",
		"cargoCarrier": "Lastebærer",
		"fromDespatchParty": "Henteaddresse",
		"fromLocation": "Fra sted",
		"goodsType": "Godstype",
		"mainDesc": "Du kan lagre søkeinstillingene som en hurtigsøk for å gjøre dette tilgjengelig i menyen. Velg hvilke av søkekriteriene som skal lagres i hurtigsøket:",
		"makePublic": "Gjør hurtigsøket tilgjengelig for andre brukere",
		"orderType": "Oppdragstype",
		"save": "Lagre",
		"searchCriteria": "Søkekriterier",
		"searchCriteriaDesc": "Velg de kriteriene du vil at hurtigsøket skal bruke:",
		"searchName": "Navn",
		"status": "Status",
		"timeIntervalAfter": "I dag | dag etter nådato | dager etter nådato",
		"timeIntervalBefore": "I dag | dag før nådato | dager før nådato",
		"title": "Lagre hurtigsøk",
		"toDeliveryParty": "Leveringsaddresse",
		"toLocation": "Til sted",
		"tripType": "Turtype",
		"cancelLabel": "Avbryt",
		"failedToSave": "Kunne ikke lagre søk",
		"failedToDelete": "Kunne ikke slette søk",
		"savedSearch": "Hurtigsøk lagret",
		"deletedSearch": "Hurtigsøk slettet",
		"pickupStatus": "Hentestatus",
		"deliveryStatus": "Leveringsstatus",
		"economicalStatus": "Økonomisk status",
		"orderStatus": "Operativ status",
		"references": "Referanser",
		"searchAreas": "Adresse / Område",
		"noDaySet": "Ingen dag satt",
		"before": "Før",
		"after": "Etter",
		"planned": "Planlagt",
		"waybillNumber": "Fraktbrevnr",
		"viewMainMenu": "Vis dette hurtigsøket i hovedmenyen",
		"viewMainMenuDesc": "Søket oppdateres kontinuerlig med 1 minutts intervall",
		"orderFilters": "Oppdragsfilter",
		"tripFilters": "Turfilter",
		"etdFrom": "ETD fra",
		"etdTo": "ETD til",
		"noLabel": "Vennligst gi hurtigsøket et navn",
		"emptyPatch": "Ingen endring gjort",
		"inboundEdi": "Inngående EDI",
		"deviation": "Avvik"
	}
}
</i18n>

<template>
	<t-dialog
		:isShown="isShown"
		:title="$t('title')"
		:confirmLabel="$t('save')"
		:cancelLabel="$t('cancelLabel')"
		disableCloseOnConfirm
		width="1000px"
		@shown="processShown"
		@close="onClose"
		@ok="onSaveSearch"
	>
		<div class="save-search-top">
			<div style="padding-bottom:10px">{{ $t('mainDesc') }}</div>
			<t-text-input
				v-model="newSettings.label"
				:label="$t('searchName')"
				width="100%"
				:isRequired="true"
				:isInvalid="missingLabel && !newSettings.label"
				:validationMessage="$t('noLabel')"
			/>
			<div class="general-search-settings">
				<t-checkbox
					v-model="newSettings.mainMenu"
					:label="$t('viewMainMenu')"
					:description="$t('viewMainMenuDesc')"
				/>
				<t-checkbox
					v-model="newSettings.isPublic"
					:label="$t('makePublic')"
				/>
			</div>
		</div>

		<div class="save-search-mid">

			<OrderFilters :orderCriteria="orderCriteria" :orderFilter="newOrderFilter"/>

			<div class="center">
				<div class="search-components">
					<div class="order-search">
						<div class="save-search-time">
							<t-input-wrapper :label="$t('orderFilters')" :description="$t('searchCriteriaDesc')"/>
							<div class="time-interval">
								<t-number-input
									v-model="orderBefore"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(orderFromDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(orderBefore))"
									hideZeroValue
									leftAlign
									min="0"
									style="grid-area: input"
								/>
								<t-switch
									v-model="orderFromDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch"
								/>
							</div>
							<div class="time-interval">
								<t-checkbox
									v-model="orderTimeInterval.useAfter"
									:label="$t('etdTo')"
									style="grid-area: checkbox"
								/>
								<t-number-input
									v-model="orderAfter"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(orderToDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(orderAfter))"
									hideZeroValue
									leftAlign
									min="0"
									style="grid-area: input"
								/>
								<t-switch
									v-model="orderToDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch"
								/>
							</div>
						</div>
						<div class="save-search-criteria">
							<div style="display: grid; grid-row-gap: 10px; grid-template-rows: repeat(4, min-content);">
								<t-switch
									v-model="orderCriteria.searchAreas"
									:label="$t('searchAreas')"
									@change="updateOrderFilter('searchAreas', $event)"
								/>
								<t-switch
									v-model="orderCriteria.fromDespatchParty"
									:label="$t('fromDespatchParty')"
									@change="updateOrderFilter('fromDespatchParty', $event)"
								/>
								<t-switch
									v-model="orderCriteria.toDeliveryParty"
									:label="$t('toDeliveryParty')"
									@change="updateOrderFilter('toDeliveryParty', $event)"
								/>
								<t-switch
									v-model="orderCriteria.customer"
									:label="$t('customer')"
									@change="updateOrderFilter('customer', $event)"
								/>
								<t-switch
									v-model="orderCriteria.goodsTypes"
									:label="$t('goodsType')"
									@change="updateOrderFilter('goodsTypes', $event)"
								/>
								<t-switch
									v-model="orderCriteria.orderTypes"
									:label="$t('orderType')"
									@change="updateOrderFilter('orderTypes', $event)"
								/>
								<t-switch
									v-model="orderCriteria.orderStatus"
									:label="$t('orderStatus')"
									@change="updateOrderFilter('orderStatus', $event)"
								/>
								<t-switch
									v-model="orderCriteria.economicalStatus"
									:label="$t('economicalStatus')"
									@change="updateOrderFilter('economicalStatus', $event)"
								/>
								<t-switch
									v-model="orderCriteria.planned"
									:label="$t('planned')"
									@change="updateOrderFilter('planned', $event)"
								/>
								<t-switch
									v-model="orderCriteria.hasAtd"
									:label="$t('pickupStatus')"
									@change="updateOrderFilter('hasAtd', $event)"
								/>
								<t-switch
									v-model="orderCriteria.references"
									:label="$t('references')"
									@change="updateOrderReferences('references', $event)"
								/>
								<t-switch
									v-model="orderCriteria.waybillNumber"
									:label="$t('waybillNumber')"
									@change="updateOrderFilter('waybillNumber', $event)"
								/>
								<t-switch
									v-model="orderCriteria.inboundEdi"
									:label="$t('inboundEdi')"
									@change="updateOrderFilter('inboundEdi', $event, '')"
								/>
								<t-switch
									v-model="orderCriteria.mustHaveDeviation"
									:label="$t('deviation')"
									@change="updateMustHaveDeviation"
								/>
							</div>
						</div>
					</div>
					<div class="trip-search">
						<div class="save-search-time">
							<t-input-wrapper :label="$t('tripFilters')" :description="$t('searchCriteriaDesc')"/>
							<div class="time-interval">
								<t-number-input
									v-model="tripBefore"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(tripFromDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(tripBefore))"
									hideZeroValue
									leftAlign
									min="0"
									style="grid-area: input"
								/>
								<t-switch
									v-model="tripFromDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch"
								/>
							</div>
							<div class="time-interval">
								<t-checkbox
									v-model="tripTimeInterval.useAfter"
									:label="$t('etdTo')"
									style="grid-area: checkbox"
								/>
								<t-number-input
									v-model="tripAfter"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(tripToDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(tripAfter))"
									hideZeroValue
									leftAlign
									min="0"
									style="grid-area: input"
								/>
								<t-switch
									v-model="tripToDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch"
								/>
							</div>
						</div>
						<div class="save-search-criteria">
							<div  style="display: grid; grid-row-gap: 10px; grid-template-rows: repeat(4, min-content);">
								<t-switch
									v-model="tripCriteria.fromLocation"
									:label="$t('fromLocation')"
									@change="updateTripFilter('fromLocation', $event)"
								/>
								<t-switch
									v-model="tripCriteria.toLocation"
									:label="$t('toLocation')"
									@change="updateTripFilter('toLocation', $event)"
								/>
								<t-switch
									v-model="tripCriteria.tripType"
									:label="$t('tripType')"
									@change="updateTripFilter('tripType', $event)"
								/>
								<t-switch
									v-model="tripCriteria.status"
									:label="$t('status')"
									@change="updateTripFilter('status', $event)"
								/>
								<t-switch
									v-model="tripCriteria.cargoCarrier"
									:label="$t('cargoCarrier')"
									@change="updateInboundEdiFilter"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<TripFilters :tripCriteria="tripCriteria" :tripFilter="newTripFilter" />

		</div>

		<template
			v-if="this.$route.params.searchId"
			v-slot:additional-buttons
		>
			<t-icon-button
				intent="danger"
				appearance="primary"
				icon="trash"
				type="bp"
				@click="onDeleteSearch"
			/>
		</template>
	</t-dialog>
</template>
<script>
import { mapActions } from './store/saveLoadPlanningSearch.store.js';
import { storageDateFormat } from '@/core';
import moment from 'moment';
import deepCopy from 'deepcopy';
import OrderFilters from './OrderFilters';
import TripFilters from './TripFilters';

export default {
	name: 'SaveSearch',
	components: { OrderFilters, TripFilters },
	props: {
		isShown: Boolean,
		orderFilter: Object,
		tripFilter: Object,
		loadedSearch: Object
	},
	data() {
		return {
			newSettings: {},
			orderTimeInterval: {},
			orderCriteria: {},
			tripTimeInterval: {},
			tripCriteria: {},
			newOrderFilter: {},
			newTripFilter: {},
			ongoingSave: false,
			missingLabel: false,
		};
	},
	computed: {
		savedOrderFilters() {
			return this.loadedSearch.orderSearch || {};
		},
		savedTripFilters() {
			return this.loadedSearch.tripSearch || {};
		},
		orderFromDateIsAfterToday: {
			get() {
				return this.orderTimeInterval.before ? (this.orderTimeInterval.before > 0) : false;
			},
			set(toggled) {
				if (toggled) {
					this.orderTimeInterval.before = Math.abs(this.orderTimeInterval.before).toString();
				} else {
					this.orderTimeInterval.before = (Math.abs(this.orderTimeInterval.before) * -1).toString();
				}
			},
		},
		orderToDateIsAfterToday: {
			get() {
				return this.orderTimeInterval.after ? (this.orderTimeInterval.after > 0) : true;
			},
			set(toggled) {
				if (toggled) {
					this.orderTimeInterval.after = Math.abs(this.orderTimeInterval.after).toString();
				} else {
					this.orderTimeInterval.after = (Math.abs(this.orderTimeInterval.after) * -1).toString();
				}
			},
		},
		tripFromDateIsAfterToday: {
			get() {
				return this.tripTimeInterval.before ? (this.tripTimeInterval.before > 0) : false;
			},
			set(toggled) {
				if (toggled) {
					this.tripTimeInterval.before = Math.abs(this.tripTimeInterval.before).toString();
				} else {
					this.tripTimeInterval.before = (Math.abs(this.tripTimeInterval.before) * -1).toString();
				}
			},
		},
		tripToDateIsAfterToday: {
			get() {
				return this.tripTimeInterval.after ? (this.tripTimeInterval.after > 0) : true;
			},
			set(toggled) {
				if (toggled) {
					this.tripTimeInterval.after = Math.abs(this.tripTimeInterval.after).toString();
				} else {
					this.tripTimeInterval.after = (Math.abs(this.tripTimeInterval.after) * -1).toString();
				}
			},
		},
		orderBefore: {
			get() {
				return this.orderTimeInterval.before ? Math.abs(this.orderTimeInterval.before).toString() : '';
			},
			set(value) {
				this.orderTimeInterval.before = (this.orderFromDateIsAfterToday ? value : value * -1).toString();
			}
		},
		orderAfter: {
			get() {
				return this.orderTimeInterval.after ? Math.abs(this.orderTimeInterval.after).toString() : '';
			},
			set(value) {
				if (value) {
					this.orderTimeInterval.after = (this.orderToDateIsAfterToday ? value : value * -1).toString();
				} else {
					this.orderTimeInterval.after = value;
				}
			}
		},
		tripBefore: {
			get() {
				return this.tripTimeInterval.before ? Math.abs(this.tripTimeInterval.before).toString() : '';
			},
			set(value) {
				this.tripTimeInterval.before = (this.tripFromDateIsAfterToday ? value : value * -1).toString();
			}
		},
		tripAfter: {
			get() {
				return this.tripTimeInterval.after ? Math.abs(this.tripTimeInterval.after).toString() : '';
			},
			set(value) {
				if (value) {
					this.tripTimeInterval.after = (this.tripToDateIsAfterToday ? value : value * -1).toString();
				} else {
					this.tripTimeInterval.after = value;
				}
			}
		},
	},
	watch: {
		'newSettings.mainMenu': function(newVal) {
			this.newSettings.autoSearch = newVal;
		}
	},
	methods: {
		...mapActions([
			'addSavedSearch',
			'deleteSavedSearch',
			'updateSavedSearch'
		]),
		processShown() {
			let orderBefore = this.diffFromToday(this.orderFilter.date.from);
			let orderAfter = this.diffFromToday(this.orderFilter.date.to);

			let tripBefore = this.diffFromToday(this.tripFilter.etd.from);
			let tripAfter = this.diffFromToday(this.tripFilter.etd.to);

			this.missingLabel = false;

			if (this.loadedSearch.id === -1) {
				this.orderTimeInterval = {
					before: orderBefore,
					after: orderAfter,
					useAfter: Boolean(orderAfter),
				};
				this.tripTimeInterval = {
					before: tripBefore,
					after: tripAfter,
					useAfter: Boolean(tripAfter),
				};
				this.orderCriteria = {
					searchAreas: Boolean(this.orderFilter.searchAreas.length),
					fromDespatchParty: Boolean(this.orderFilter.fromDespatchParty.length),
					toDeliveryParty: Boolean(this.orderFilter.toDeliveryParty.length),
					customer: Boolean(this.orderFilter.customer.length),
					goodsTypes: Boolean(this.orderFilter.goodsTypes.length),
					orderTypes: Boolean(this.orderFilter.orderTypes.length),
					economicalStatus: Boolean(this.orderFilter.economicalStatus.length),
					orderStatus: Boolean(this.orderFilter.orderStatus.length),
					references: Boolean(this.orderFilter.referencesText.length),
					planned: this.orderFilter.planned === true,
					hasAtd: this.orderFilter.hasAtd && this.orderFilter.hasAtd.value !== undefined,
					waybillNumber: Boolean(this.orderFilter.waybillNumber.length),
					inboundEdi: Boolean(this.orderFilter.mustHaveInboundEdi || this.orderFilter.inboundEdi),
					mustHaveDeviation: Boolean(this.orderFilter.mustHaveDeviation),
				};
				this.tripCriteria = {
					fromLocation: Boolean(this.tripFilter.fromLocation.length),
					toLocation: Boolean(this.tripFilter.toLocation.length),
					tripType: Boolean(this.tripFilter.tripType.length),
					status: Boolean(this.tripFilter.status.length),
					cargoCarrier: Boolean(this.tripFilter.cargoCarrier.length)
				};

				this.newOrderFilter = deepCopy(this.orderFilter);
				if (this.orderFilter.fromDespatchParty)
					this.newOrderFilter.fromDespatchParty = this.orderFilter.fromDespatchParty.map(x => !x.notRemovable);
				if (this.orderFilter.toDeliveryParty)
					this.newOrderFilter.toDeliveryParty = this.orderFilter.toDeliveryParty.map(x => !x.notRemovable);
				this.newTripFilter = deepCopy(this.tripFilter);

				this.newSettings = {
					label: '',
					mainMenu: true,
					isPublic: false,
					autoSearch: true,
				};
			} else {
				this.newSettings = {
					label: this.loadedSearch.label,
					mainMenu: this.loadedSearch.mainMenu,
					isPublic: this.loadedSearch.isPublic,
					autoSearch: this.loadedSearch.autoSearch,
				};
				this.orderTimeInterval = { ...this.loadedSearch.orderSearch.timeInterval, before: orderBefore, after: orderAfter };
				this.orderTimeInterval.useAfter = Boolean(this.orderTimeInterval.after);

				this.tripTimeInterval = { ...this.loadedSearch.tripSearch.timeInterval, before: tripBefore, after: tripAfter };
				this.tripTimeInterval.useAfter = Boolean(this.tripTimeInterval.after);
				this.orderCriteria = {
					searchAreas: this.savedOrderFilters.searchAreas && this.savedOrderFilters.searchAreas.length > 0,
					fromDespatchParty: this.savedOrderFilters.fromDespatchParty && this.savedOrderFilters.fromDespatchParty.length > 0,
					toDeliveryParty: this.savedOrderFilters.toDeliveryParty && this.savedOrderFilters.toDeliveryParty.length > 0,
					customer: this.savedOrderFilters.customer && this.savedOrderFilters.customer.length > 0,
					goodsTypes: this.savedOrderFilters.goodsTypes && this.savedOrderFilters.goodsTypes.length > 0,
					orderTypes: this.savedOrderFilters.orderTypes && this.savedOrderFilters.orderTypes.length > 0,
					economicalStatus: this.savedOrderFilters.economicalStatus && this.savedOrderFilters.economicalStatus.length > 0,
					orderStatus: this.savedOrderFilters.orderStatus && this.savedOrderFilters.orderStatus.length > 0,
					references: this.savedOrderFilters.references && this.savedOrderFilters.references.length > 0,
					planned: this.savedOrderFilters.planned === true,
					hasAtd: this.savedOrderFilters.hasAtd && this.savedOrderFilters.hasAtd.value !== undefined,
					waybillNumber: this.savedOrderFilters.waybillNumber && this.savedOrderFilters.waybillNumber.length > 0,
					inboundEdi: Boolean(this.savedOrderFilters.mustHaveInboundEdi || this.savedOrderFilters.inboundEdi),
					mustHaveDeviation: Boolean(this.savedOrderFilters.mustHaveDeviation),
				};
				this.tripCriteria = {
					fromLocation: this.savedTripFilters.fromLocation && this.savedTripFilters.fromLocation.length > 0,
					toLocation: this.savedTripFilters.toLocation && this.savedTripFilters.toLocation.length > 0,
					tripType: this.savedTripFilters.tripType && this.savedTripFilters.tripType.length > 0,
					status: this.savedTripFilters.status && this.savedTripFilters.status.length > 0,
					cargoCarrier: this.savedTripFilters.cargoCarrier && this.savedTripFilters.cargoCarrier.length > 0
				};

				this.newOrderFilter = deepCopy(this.orderFilter);
				this.newTripFilter = deepCopy(this.tripFilter);
			}
		},
		updateOrderReferences(key, checked) {
			this.updateOrderFilter(key, checked);
			this.newOrderFilter['referencesText'] = checked ? deepCopy(this.orderFilter['referencesText']) : [];
		},
		updateOrderFilter(key, checked, defaultValue = []) {
			this.newOrderFilter[key]
				= checked
					? deepCopy(this.orderFilter[key])
					: defaultValue;
		},
		updateInboundEdiFilter(checked) {

			if (checked) {
				this.newSettings.mustHaveInboundEdi = true;
				this.newSettings.inboundEdi = this.searchFilter.inboundEdi;

			} else {
				this.newSettings.mustHaveInboundEdi = false;
				this.newSettings.inboundEdi = '';
			}
		},
		updateMustHaveDeviation(checked) {

			this.newSettings.mustHaveDeviation = checked;
		},
		updateTripFilter(key, checked) {
			if (checked) {
				this.newTripFilter[key] = deepCopy(this.tripFilter[key]);
			} else {
				this.newTripFilter[key] = [];
			}
		},
		diffFromToday(date) {
			if (date)
				return moment(date, storageDateFormat).startOf('day').diff(moment().startOf('day'), 'days').toString();
			return '';
		},
		loadPlanningRoute(searchId = -1) {
			let defaultView = localStorage.getItem(`loadPlanning/-1/defaultView`);
			localStorage.setItem(`loadPlanning/${searchId}/defaultView`, defaultView);
			this.$router.push({
				name: defaultView === 'map' ? 'savedLoadPlanningMap' : 'savedLoadPlanningList',
				params: { searchId }
			});
		},
		async onSaveSearch() {
			if (this.ongoingSave) return;
			this.$notify({ clean: true });
			this.ongoingSave = true;

			if (!this.newSettings.label) {
				this.$notify({
					type: 'error',
					title: this.$t('noLabel'),
				});
				this.missingLabel = true;
				this.ongoingSave = false;
				return;
			}
			this.missingLabel = false;

			let { searchId } = this.$route.params;
			try {
				let searchOptions = {
					...this.newSettings,
					orderSearch: {
						...this.newOrderFilter,
						timeInterval: { ...this.orderTimeInterval },
					},
					tripSearch: {
						...this.newTripFilter,
						timeInterval: { ...this.tripTimeInterval },
					},
				};

				if (searchId)
					await this.updateSavedSearch({ searchId, searchOptions, orderFilter: this.orderFilter, tripFilter: this.tripFilter });
				else {
					let { id } = await this.addSavedSearch({ searchId: -1, searchOptions, orderFilter: this.orderFilter, tripFilter: this.tripFilter });
					this.loadPlanningRoute(id);
				}

				this.$notify({
					type: 'success',
					title: `${this.$t('savedSearch')}: ${searchOptions.label}`
				});
				this.$emit('close');
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$te(e.message) ? this.$t(e.message) : this.$t('failedToSave'),
				});
			}
			this.ongoingSave = false;
		},
		async onDeleteSearch() {
			this.$notify({ clean: true });
			try {
				await this.deleteSavedSearch(this.$route.params.searchId);
				this.$emit('delete');
				this.$notify({
					type: 'success',
					title: `${this.$t('deletedSearch')}: ${this.newSettings.label}`
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$t('failedToDelete')
				});
			}
		},
		onClose() {
			this.$notify({ clean: true });
			this.$emit('close');
		},
	}
};
</script>

<style lang="scss" scoped>
	@import '~@/components/evergreen/scss/colors';
	@import '~@/components/evergreen/scss/text';

.save-search-top {
	border-bottom: solid 1px #e4e7eb;
}
.save-search-mid {
	padding-top: 10px;
	display: grid;
	grid-template-columns: 175px auto 175px;
	grid-column-gap: 20px;
}
.save-search-time {
	padding-bottom: 15px;
	display: grid;
	grid-template-rows: repeat(2, auto);
	grid-row-gap: 20px;
}
.time-interval {
	display: grid;
	grid-column-gap: 10px;
	grid-template-columns: minmax(76px, max-content) max-content;
	grid-template-areas:
		"checkbox input"
		"....... switch";
}
.save-search-criteria {
	padding-bottom: 15px;
	border-bottom: solid 1px #e4e7eb;
	display: flex;
	flex-direction: column;
}
.order-search {
	padding-left: 10px;
	padding-right: 10px;
	border-left: solid 1px #e4e7eb;
	border-right: solid 1px #e4e7eb;
}
.trip-search {
	padding-left: 10px;
	padding-right: 10px;
	border-right: solid 1px #e4e7eb;
}
.search-components {
	display: grid;
	grid-template-columns: repeat(2, auto);
}

.search-criteria-label {
	user-select: none;
	font-size: 11px;
	font-weight: 600;
	line-height: 1.45;
	letter-spacing: 0.3px;
	color: get-color($text, 'muted');
	text-transform: uppercase;
	margin-bottom: 8px;
}
.search-criteria {
	@extend %text-small;
	@extend %font-families-ui;
	font-weight: 600;
	user-select: none;
	line-height: 20px;
	color: get-color($text, 'dark');
	box-sizing: border-box;
	&.disabled {
		color: get-color($text, 'muted');
	}
}
.search-criteria-description {
	@extend %text-small;
	@extend %font-families-ui;
	user-select: none;
	color: get-color($text, 'muted');
	box-sizing: border-box;
	margin-top: 0px;
	margin-bottom: 25px;
}

.general-search-settings {
	margin: 10px 5px 20px 5px;
	display: flex;
	* {
		margin-right: 20px;
	}
}
</style>
